import { Link } from "@tanstack/react-router";
import { type FormEvent, useId, useState } from "react";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";
import type { FauteJeu, LivreJeuDetail, RapportJeu, SceneJeuResume } from "./contract";
import { quandEnClair } from "./dates-en-clair";
import { formatDate } from "./format";
import {
  ALERTE_GRISE,
  ALERTE_JAUNE,
  BadgeOrigine,
  BOUTON_CONTOUR,
  Carte,
  CHAMP,
  ErreurLivresJeux,
  FilLivresJeux,
  IntrouvableLivresJeux,
  LIEN_TEXTE,
} from "./livres-jeux-commun";
import { EditeurTexte, useTexteJeu } from "./livres-jeux-editeur";
import { OngletLivre } from "./livres-jeux-outils";
import { OngletPublication } from "./livres-jeux-publication";
import { OngletReglages } from "./livres-jeux-reglages";
import {
  messageDEchec,
  useCreerScene,
  useLivreJeu,
  useOrdonnerScenes,
  useVerification,
} from "./livres-jeux-requetes";
import {
  ciblesInconnues,
  cleDepuisTitre,
  compterJeu,
  deplacer,
  LIBELLE_FIN,
  LIBELLE_ORIGINE,
  LIBELLE_QUOI,
  refusDeCle,
  titreDeScene,
} from "./livres-jeux-textes";
import { TextesAvant } from "./livres-jeux-textes-avant";
import { ConsoleShell } from "./shell";
import { affichageDeLecture, Chargement, idValide, RelectureEchouee } from "./states";

/**
 * Un livre-jeu (`/livres-jeux/livres/$id?onglet=...`, spec du 03/10/2026) : en tete ce qu'il
 * en est (scenes, scenes ecrites, depart, publication), puis cinq onglets. Les scenes, par
 * defaut, dans l'ordre du livre ; la trame ; les reglages ; le livre entier (verifier, mettre
 * en page, exporter, importer) ; la publication vers l'appli compagnon.
 */

export type OngletJeu = "scenes" | "trame" | "reglages" | "livre" | "publication";

export const ONGLETS_JEU: readonly { readonly onglet: OngletJeu; readonly libelle: string }[] = [
  { onglet: "scenes", libelle: "Scènes" },
  { onglet: "trame", libelle: "Trame" },
  { onglet: "reglages", libelle: "Réglages" },
  { onglet: "livre", libelle: "Livre" },
  { onglet: "publication", libelle: "Publication" },
];

export function LivresJeuxLivrePage({ id, onglet }: { id: string; onglet: OngletJeu }) {
  const valide = idValide(id);
  const lecture = useLivreJeu(id, valide);
  const affichage = affichageDeLecture(lecture);
  const detail = affichage.contenu ? lecture.data : undefined;

  return (
    <ConsoleShell connecte={affichage.contenu}>
      <FilLivresJeux>{detail?.livre.titre}</FilLivresJeux>
      {!valide ? <IntrouvableLivresJeux quoi={`le livre ${id}`} /> : null}
      {valide && lecture.isPending ? <Chargement quoi="du livre" /> : null}
      {affichage.ecranDErreur ? (
        <ErreurLivresJeux error={lecture.error} quoi={`le livre ${id}`} />
      ) : null}
      {affichage.bandeau ? <RelectureEchouee error={lecture.error} /> : null}
      {detail === undefined ? null : <Livre detail={detail} onglet={onglet} />}
    </ConsoleShell>
  );
}

function Livre({ detail, onglet }: { detail: LivreJeuDetail; onglet: OngletJeu }) {
  const { livre, scenes, publication } = detail;
  // La version des reglages que ce formulaire vient d'ecrire : il se remonte sur elle.
  const [reglagesEnregistres, setReglagesEnregistres] = useState<number | null>(null);
  const ecrites = scenes.filter((scene) => scene.provenance !== null).length;
  const mots = scenes.reduce((total, scene) => total + scene.mots, 0);
  return (
    <>
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold [overflow-wrap:anywhere]">{livre.titre}</h1>
          <p className="mt-1 font-mono text-xs text-muted-foreground">{livre.cle}</p>
        </div>
        <ul aria-label="Où en est le livre" className="flex flex-wrap gap-2 text-sm">
          <li className="rounded-full bg-muted px-3 py-1 tabular-nums">
            {scenes.length === 0
              ? "aucune scène"
              : `${compterJeu(ecrites, "scène écrite", "scènes écrites")} sur ${scenes.length}`}
          </li>
          <li className="rounded-full bg-muted px-3 py-1 tabular-nums">
            {compterJeu(mots, "mot", "mots")}
          </li>
          <li className="rounded-full bg-muted px-3 py-1">
            {publication === null
              ? "jamais publié"
              : publication.recu
                ? `publié le ${formatDate(publication.publie_le)}`
                : "publication refusée"}
          </li>
        </ul>
      </header>

      <nav
        aria-label="Onglets du livre"
        className="flex gap-0.5 overflow-x-auto border-b border-border"
      >
        {ONGLETS_JEU.map((entree) => (
          <Link
            activeOptions={{ exact: true, includeSearch: true }}
            className={cn(
              "-mb-px whitespace-nowrap border-b-2 px-3.5 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              entree.onglet === onglet
                ? "border-foreground font-semibold text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
            key={entree.onglet}
            params={{ id: String(livre.id) }}
            search={{ onglet: entree.onglet }}
            to="/livres-jeux/livres/$id"
          >
            {entree.libelle}
          </Link>
        ))}
      </nav>

      {onglet === "scenes" ? <OngletScenes detail={detail} /> : null}
      {onglet === "trame" ? <OngletTrame detail={detail} key={`trame-${livre.id}`} /> : null}
      {onglet === "reglages" ? (
        <OngletReglages
          detail={detail}
          enregistre={reglagesEnregistres === livre.reglages_version}
          key={`reglages-${livre.reglages_version}`}
          quandEnregistre={setReglagesEnregistres}
        />
      ) : null}
      {onglet === "livre" ? <OngletLivre detail={detail} /> : null}
      {onglet === "publication" ? <OngletPublication detail={detail} /> : null}
    </>
  );
}

// ---------------------------------------------------------------------------------------
// Les scenes

/** Les fautes du dernier rapport, scene par scene. */
function fautesParScene(rapport: RapportJeu | undefined): Map<number, FauteJeu[]> {
  const parScene = new Map<number, FauteJeu[]>();
  for (const faute of rapport?.erreurs ?? []) {
    if (faute.scene_id === null) continue;
    parScene.set(faute.scene_id, [...(parScene.get(faute.scene_id) ?? []), faute]);
  }
  return parScene;
}

function OngletScenes({ detail }: { detail: LivreJeuDetail }) {
  const { livre, scenes, publication } = detail;
  const verification = useVerification(livre.id);
  const ordonner = useOrdonnerScenes(livre.id);
  const fautes = fautesParScene(verification.data);
  const cles = new Set(scenes.map((scene) => scene.cle));
  const ordre = [...scenes].sort((a, b) => a.rang - b.rang);

  const deplacerScene = (scene: SceneJeuResume, sens: -1 | 1): void => {
    const nouvelOrdre = deplacer(scenes, scene.id, sens);
    if (nouvelOrdre !== null && !ordonner.isPending) ordonner.mutate(nouvelOrdre);
  };

  return (
    <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_20rem]">
      <section aria-labelledby="livres-jeux-scenes" className="flex min-w-0 flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-semibold" id="livres-jeux-scenes">
            {compterJeu(scenes.length, "scène", "scènes", "Aucune scène")}, dans l'ordre du livre
          </h2>
          <button
            className={BOUTON_CONTOUR}
            disabled={verification.isFetching || scenes.length === 0}
            onClick={() => void verification.refetch()}
            type="button"
          >
            {verification.isFetching ? "Vérification..." : "Vérifier le livre"}
          </button>
        </div>
        {verification.isError ? (
          <p className="text-sm text-destructive" role="alert">
            {messageDEchec(verification.error)}
          </p>
        ) : null}
        {verification.data ? (
          <p className={verification.data.ok ? ALERTE_GRISE : ALERTE_JAUNE} role="status">
            {verification.data.ok
              ? "Vérifié : aucune faute."
              : `${compterJeu(verification.data.erreurs.length, "faute", "fautes")} à corriger, montrées sous chaque scène (le détail est dans l'onglet Livre).`}
          </p>
        ) : null}
        {publication?.recu ? (
          <p className={ALERTE_GRISE}>
            Ce livre est publié : changer l'ordre des scènes change les codes de sauvegarde, et un
            livre déjà imprimé ne correspondrait plus.
          </p>
        ) : null}
        {ordonner.isError ? (
          <p className="text-sm text-destructive" role="alert">
            {messageDEchec(ordonner.error)}
          </p>
        ) : null}
        {ordre.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Ajoute une scène, ou importe un livre entier en YAML depuis l'onglet Livre.
          </p>
        ) : (
          <ol aria-labelledby="livres-jeux-scenes" className="flex flex-col gap-2">
            {ordre.map((scene, rang) => (
              <LigneScene
                cles={cles}
                depart={livre.depart === scene.cle}
                deplacer={deplacerScene}
                derniere={rang === ordre.length - 1}
                enCours={ordonner.isPending}
                fautes={fautes.get(scene.id) ?? []}
                key={scene.id}
                premiere={rang === 0}
                scene={scene}
              />
            ))}
          </ol>
        )}
      </section>
      <div className="flex flex-col gap-5">
        <NouvelleScene livreId={livre.id} />
        <Journal detail={detail} />
      </div>
    </div>
  );
}

function LigneScene({
  scene,
  depart,
  fautes,
  cles,
  premiere,
  derniere,
  enCours,
  deplacer: deplacerScene,
}: {
  scene: SceneJeuResume;
  depart: boolean;
  fautes: readonly FauteJeu[];
  cles: ReadonlySet<string>;
  premiere: boolean;
  derniere: boolean;
  enCours: boolean;
  deplacer: (scene: SceneJeuResume, sens: -1 | 1) => void;
}) {
  const inconnues = ciblesInconnues(scene, cles);
  return (
    <li
      className={cn(
        "flex flex-col gap-2 rounded-lg border bg-card p-3.5 text-card-foreground md:p-4",
        fautes.length > 0 ? "border-destructive/50" : "border-border",
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <Link
            className="font-semibold underline-offset-2 hover:underline [overflow-wrap:anywhere] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            params={{ id: String(scene.id) }}
            to="/livres-jeux/scenes/$id"
          >
            {titreDeScene(scene)}
          </Link>
          <p className="font-mono text-xs text-muted-foreground">{scene.cle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {depart ? (
            <span className="rounded-full bg-foreground px-2.5 py-0.5 text-xs font-medium text-background">
              départ
            </span>
          ) : null}
          {scene.fin === null ? null : (
            <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs">
              {LIBELLE_FIN[scene.fin]}
            </span>
          )}
          {scene.page === null ? null : (
            <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs">page {scene.page}</span>
          )}
          <BadgeOrigine origine={scene.provenance} />
        </div>
      </div>
      {scene.extrait === "" ? (
        <p className="text-sm italic text-muted-foreground">Pas encore de texte.</p>
      ) : (
        <p className="text-sm text-muted-foreground [overflow-wrap:anywhere]">
          {scene.extrait}{" "}
          <span className="whitespace-nowrap">({compterJeu(scene.mots, "mot", "mots")})</span>
        </p>
      )}
      {scene.choix.length === 0 ? null : (
        <ul className="flex flex-col gap-0.5 text-sm">
          {scene.choix.map((choix) => (
            <li className="[overflow-wrap:anywhere]" key={choix.id}>
              <span className="font-medium">{choix.libelle || choix.id}</span>
              {choix.enigme ? <span className="text-muted-foreground"> (énigme)</span> : null}
              {" → "}
              <span className="font-mono text-xs">{choix.cibles.join(", ") || "?"}</span>
            </li>
          ))}
        </ul>
      )}
      {inconnues.length === 0 ? null : (
        <p className="text-xs text-fourchette">
          Mène à {inconnues.length > 1 ? "des scènes" : "une scène"} qui n'existe pas encore :{" "}
          <span className="font-mono">{inconnues.join(", ")}</span>
        </p>
      )}
      {fautes.length === 0 ? null : (
        <ul className="flex list-disc flex-col gap-0.5 pl-5 text-sm text-destructive">
          {fautes.map((faute, rang) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: le rapport n'a pas d'identifiants
            <li className="[overflow-wrap:anywhere]" key={rang}>
              {faute.message}
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          aria-label={`Monter ${titreDeScene(scene)}`}
          className={cn(BOUTON_CONTOUR, "min-h-8 px-3 py-1")}
          disabled={premiere || enCours}
          onClick={() => deplacerScene(scene, -1)}
          type="button"
        >
          ↑ Monter
        </button>
        <button
          aria-label={`Descendre ${titreDeScene(scene)}`}
          className={cn(BOUTON_CONTOUR, "min-h-8 px-3 py-1")}
          disabled={derniere || enCours}
          onClick={() => deplacerScene(scene, 1)}
          type="button"
        >
          ↓ Descendre
        </button>
      </div>
    </li>
  );
}

function NouvelleScene({ livreId }: { livreId: number }) {
  const creer = useCreerScene(livreId);
  const [titre, setTitre] = useState("");
  const [cleSaisie, setCleSaisie] = useState<string | null>(null);
  const [tente, setTente] = useState(false);
  const idTitre = useId();
  const idCle = useId();
  const cle = cleSaisie ?? cleDepuisTitre(titre);
  const refus = refusDeCle(cle);

  const envoyer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setTente(true);
    if (refus !== null || creer.isPending) return;
    creer.mutate(
      { cle, titre: titre.trim() === "" ? null : titre.trim() },
      {
        onSuccess: () => {
          setTitre("");
          setCleSaisie(null);
          setTente(false);
        },
      },
    );
  };

  return (
    <Carte id="livres-jeux-nouvelle-scene" titre="Nouvelle scène">
      <form className="flex flex-col gap-3" noValidate onSubmit={envoyer}>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold" htmlFor={idTitre}>
            Titre
          </label>
          <input
            className={CHAMP}
            id={idTitre}
            maxLength={200}
            onChange={(evenement) => setTitre(evenement.target.value)}
            placeholder="Le grenier"
            value={titre}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold" htmlFor={idCle}>
            Identifiant
          </label>
          <input
            aria-describedby={`${idCle}-aide`}
            aria-invalid={(tente && refus !== null) || undefined}
            autoCapitalize="none"
            autoCorrect="off"
            className={cn(CHAMP, "font-mono")}
            id={idCle}
            maxLength={60}
            onChange={(evenement) => setCleSaisie(evenement.target.value)}
            placeholder="grenier"
            spellCheck={false}
            value={cle}
          />
          <p className="text-xs text-muted-foreground" id={`${idCle}-aide`}>
            Ce que les choix des autres scènes écrivent après « va : ».
          </p>
          {tente && refus !== null ? <p className="text-sm text-destructive">{refus}</p> : null}
        </div>
        {creer.isError ? (
          <p className="text-sm text-destructive" role="alert">
            {messageDEchec(creer.error)}
          </p>
        ) : null}
        <Button
          aria-disabled={creer.isPending || undefined}
          className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
          type="submit"
        >
          Ajouter à la fin
        </Button>
      </form>
    </Carte>
  );
}

/** Les dernieres ecritures : qui, quoi, quand. */
function Journal({ detail }: { detail: LivreJeuDetail }) {
  const maintenant = Date.now();
  if (detail.journal.length === 0) return null;
  return (
    <Carte id="livres-jeux-journal" titre="Dernières écritures">
      <ul className="flex flex-col gap-1.5 text-sm">
        {detail.journal.slice(0, 12).map((ligne) => (
          <li className="[overflow-wrap:anywhere]" key={ligne.id}>
            <span className="text-muted-foreground">{quandEnClair(ligne.le, maintenant)}</span>{" "}
            {ligne.par === "claude" ? LIBELLE_ORIGINE.claude : LIBELLE_ORIGINE.nicolas}{" "}
            {LIBELLE_QUOI[ligne.quoi]}
            {ligne.scene_cle === null ? null : (
              <span className="font-mono text-xs"> {ligne.scene_cle}</span>
            )}
          </li>
        ))}
      </ul>
    </Carte>
  );
}

// ---------------------------------------------------------------------------------------
// La trame

function OngletTrame({ detail }: { detail: LivreJeuDetail }) {
  const { livre } = detail;
  const cible = { type: "trame" as const, livreId: livre.id };
  const texte = useTexteJeu(cible, {
    texte: livre.trame,
    version: livre.trame_version,
    provenance: livre.trame_provenance,
  });
  return (
    <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="min-w-0 md:rounded-lg md:border md:border-border md:bg-card md:p-5">
        <EditeurTexte
          cible={cible}
          invite="Le plan du livre, en clair : l'enquête, les lieux, les indices, les fins. Tout s'enregistre pendant que tu tapes."
          quoi="la trame"
          texte={texte}
          titre="Trame"
        />
      </div>
      <aside className={cn(ALERTE_GRISE, "flex flex-col gap-2")}>
        <p>
          <strong className="font-semibold">La trame est à toi.</strong> Claude la lit avant
          d'écrire une scène, il n'y écrit jamais.
        </p>
        <p>
          Les scènes se créent dans l'onglet Scènes, ou d'un coup par un YAML entier (onglet Livre).
          Le guide du chapitre :{" "}
          <Link
            className={LIEN_TEXTE}
            params={{ slug: "livres-jeux", nom: "trame-du-chapitre" }}
            to="/portefeuille/idees/$slug/gestes/$nom"
          >
            écrire la trame
          </Link>
          .
        </p>
      </aside>
      <div className="min-w-0 xl:col-span-2">
        <TextesAvant livreId={livre.id} scene={null} />
      </div>
    </div>
  );
}
